/**
 * Where a certificate stands, on the two axes that matter.
 *
 * The screen showed one: obtained, pending, failed. That answers "did it come
 * back" and says nothing about "is it still good" — and a dossier closes with
 * documents that were valid when they arrived and expired while the rest of
 * the case was being assembled. A matrícula lasts thirty days; a case takes
 * longer than that more often than not.
 *
 * So there are two answers, always given together:
 *
 * - **obtenção** — did the document come, is it coming, or will it never come
 *   by itself;
 * - **vigência** — is it still accepted, about to stop being, or already past.
 *
 * Neither implies the other. A certificate can be obtained and expired, which
 * is the combination that ruins a closing, and the one a single status hides.
 */
import type { CatalogItem, ItemNature } from './catalog-item'

/** Did the document come back — and can it, at all. */
export type ObtentionState =
  /** Nobody has gone for it yet. */
  | 'pending'
  /** A robot is at the portal right now. */
  | 'running'
  /** It came back and was read. */
  | 'obtained'
  /** The portal answered, but with a pendency rather than a document. */
  | 'issue'
  /** The attempt failed — a layout change, a portal down. Worth retrying. */
  | 'failed'
  /** The portal demands a field the case does not have. A person must fill it. */
  | 'missingData'
  /**
   * The órgão answered and declined to issue, because there are debts.
   *
   * Not a failure: it is the finding the diligence went looking for, and the
   * queue must leave it alone.
   */
  | 'refused'
  /** No robot fetches this one: it is on a person's desk. */
  | 'manual'

/**
 * The four answers a person reads first, before any detail.
 *
 * The eight obtention states are what the queue needs to act on; a reader of
 * the dossier needs four, and every one of the eight falls in exactly one:
 *
 * - **obtida** — the document came back and was read (`obtained`);
 * - **indisponível** — no document, and waiting will not bring one: the portal
 *   failed, demands a field the case lacks, or the órgão declined to issue
 *   (`failed`, `missingData`, `refused`);
 * - **pendente** — no document *yet*, and the way to one is under way or on a
 *   desk (`pending`, `running`, `issue`, `manual`);
 * - **divergência** — the document came back, but its reading conflicts with
 *   another source about the same subject (see `Divergence`). It outranks
 *   "obtida": a reading that conflicts is not settled, however clean it looks.
 */
export type CertificateState = 'obtained' | 'unavailable' | 'pending' | 'divergent'

export const CERTIFICATE_STATES: readonly CertificateState[] = ['obtained', 'divergent', 'pending', 'unavailable']

export const CERTIFICATE_STATE_LABEL: Record<CertificateState, string> = {
  obtained: 'Obtida',
  unavailable: 'Indisponível',
  pending: 'Pendente',
  divergent: 'Divergência'
}

/**
 * Why an obtained reading is not settled.
 *
 * - `unconfirmedSubject`: the certificate reports an occurrence (warning or
 *   adverse) and the subject's own document was not found in it. The reading
 *   and the identity of the subject disagree — it may be a homonym's.
 * - `conflictingReading`: another certificate of the same subject that answers
 *   **the very same question** — the sibling it mirrors, or a second line of
 *   the same document — was read with a different verdict. Certificates that
 *   merely answer the same catalogue item from different courts are not this:
 *   a clean bankruptcy search next to a civil suit is two answers, not a
 *   conflict.
 */
export type Divergence = 'unconfirmedSubject' | 'conflictingReading'

export const DIVERGENCE_LABEL: Record<Divergence, string> = {
  unconfirmedSubject: 'Ocorrência sem confirmação de que é do pesquisado',
  conflictingReading: 'Leitura diferente da certidão que responde a mesma consulta'
}

/**
 * The reading of an obtained certificate, in the words of the dossier.
 *
 * Not "negativa" and "positiva": those are names of kinds of certificate, not
 * results. A *positiva com efeito de negativa* is regular, and calling the
 * result "negativa" is how a reader takes one for the other (DIL-059).
 */
export type Reading = 'clear' | 'warning' | 'adverse'

export const READING_LABEL: Record<Reading, string> = {
  clear: 'Regular',
  warning: 'Atenção',
  adverse: 'Com restrição'
}

/**
 * The reading of a registry datum (IM-04, IM-05, PJ-01).
 *
 * A dado cadastral is not a certidão de situação: a clean one is not
 * "regular", it simply informs. Calling it regular would certify something no
 * órgão certified.
 */
export const REGISTRY_READING_LABEL: Record<Reading, string> = {
  clear: 'Informativo',
  warning: 'Atenção',
  adverse: 'Com restrição'
}

/** The word for a reading, by what kind of document gave it. */
export const readingLabelFor = (reading: Reading, nature: ItemNature): string =>
  nature === 'registry' ? REGISTRY_READING_LABEL[reading] : READING_LABEL[reading]

/** Is it still accepted. */
export type ValidityState =
  /** Well inside its window. */
  | 'valid'
  /** Close enough to expiry to be worth reissuing before closing. */
  | 'expiring'
  /** Past its date — it will not be accepted at the closing. */
  | 'expired'
  /**
   * A snapshot of the day it was taken.
   *
   * It does not expire by any rule, and precisely for that reason the matrix
   * demands it be reconfirmed at the signing: an indisponibilidade entered the
   * day after the consultation is invisible to it.
   */
  | 'snapshot'
  /** Issued once, or good until superseded — no countdown applies. */
  | 'notApplicable'
  /** There is no document, or it carries no issue date to count from. */
  | 'unknown'

export const OBTENTION_LABEL: Record<ObtentionState, string> = {
  pending: 'Pendente',
  running: 'Em obtenção',
  obtained: 'Obtida',
  issue: 'Pendência no órgão',
  failed: 'Erro na coleta',
  missingData: 'Falta dado que o portal exige',
  refused: 'Órgão não emitiu — há pendências',
  manual: 'Emissão manual'
}

export const VALIDITY_LABEL: Record<ValidityState, string> = {
  valid: 'Vigente',
  expiring: 'Vencendo',
  expired: 'Vencida',
  snapshot: 'Retrato da data — reconfirmar na lavratura',
  notApplicable: 'Sem prazo',
  unknown: 'Sem data de emissão'
}

/**
 * How close to the end is close enough to act.
 *
 * Proportional, because seven days left of a thirty-day matrícula is urgent
 * and seven days left of a hundred-and-eighty-day CND is not. The floor keeps
 * very short windows from being declared "expiring" the moment they are
 * issued.
 */
const WARNING_FRACTION = 0.2
const WARNING_FLOOR_DAYS = 5

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const daysBetween = (from: Date, to: Date): number =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000)

export type CertificateStanding = {
  /** The one of four a reader sees first. */
  state: CertificateState
  /** Why an obtained reading is not settled, when it is not. */
  divergence: Divergence | null
  obtention: ObtentionState
  validity: ValidityState
  /** When it stops being accepted, when that can be computed. */
  expiresOn: Date | null
  /** Days left — negative once past. `null` when nothing expires. */
  daysLeft: number | null
  /**
   * Still accepted today, and no longer accepted on the expected closing.
   *
   * The state the chip calls "vence antes do fechamento": the one to reissue
   * now, while there is time, rather than on the morning of the deed. False
   * when the case has no closing date, or when it already expired.
   */
  expiresBeforeClosing?: boolean
}

/**
 * Reads both axes for one certificate.
 *
 * The catalogue item is what knows the validity rule, and it may be absent:
 * a certificate whose name has drifted away from the pairing table still has
 * an obtention state, and says so, rather than disappearing from the screen.
 */
export const standingOf = (
  certificate: {
    status: string
    automated: boolean
    issuedOn: string | null
    filePath: string | null
    failureKind?: string | null
  },
  item: CatalogItem | null,
  now: Date,
  /** What the subject's other certificates say against this one — `Subject.divergenceOf`. */
  divergence: Divergence | null = null,
  /** The expected closing, when the case has one — what "before closing" is measured against. */
  options: { closingOn?: Date | null } = {}
): CertificateStanding => {
  const obtention = obtentionOf(certificate)
  const settled = obtention === 'obtained' ? divergence : null
  const head = { state: stateOf(obtention, settled), divergence: settled }

  if (!item || !certificate.issuedOn) {
    return {
      ...head,
      obtention,
      validity: item && obtention === 'obtained' ? validityWithoutDate(item) : 'unknown',
      expiresOn: null,
      daysLeft: null
    }
  }

  const issuedOn = new Date(`${certificate.issuedOn}T12:00:00`)

  if (Number.isNaN(issuedOn.getTime())) {
    return { ...head, obtention, validity: 'unknown', expiresOn: null, daysLeft: null }
  }

  const expiresOn = item.expiresOn(issuedOn)

  if (!expiresOn) {
    return { ...head, obtention, validity: validityWithoutDate(item), expiresOn: null, daysLeft: null }
  }

  const window = Math.max(daysBetween(issuedOn, expiresOn), 1)
  const daysLeft = daysBetween(now, expiresOn)
  const warningAt = Math.max(Math.round(window * WARNING_FRACTION), WARNING_FLOOR_DAYS)

  const validity: ValidityState = daysLeft < 0 ? 'expired' : daysLeft <= warningAt ? 'expiring' : 'valid'

  return {
    ...head,
    obtention,
    validity,
    expiresOn,
    daysLeft,
    // Compared by day: what expires on the closing day itself is still good
    // at the signing.
    expiresBeforeClosing:
      validity !== 'expired' && Boolean(options.closingOn) && daysBetween(options.closingOn!, expiresOn) < 0
  }
}

const UNAVAILABLE: readonly ObtentionState[] = ['failed', 'missingData', 'refused']

/** Which of the four a reader sees, from the obtention state and the divergence. */
export const stateOf = (obtention: ObtentionState, divergence: Divergence | null): CertificateState => {
  if (obtention === 'obtained') return divergence ? 'divergent' : 'obtained'
  if (UNAVAILABLE.includes(obtention)) return 'unavailable'

  return 'pending'
}

/** What the validity means for an item that never counts days. */
const validityWithoutDate = (item: CatalogItem): ValidityState =>
  item.validity.kind === 'snapshot' ? 'snapshot' : item.validity.kind === 'days' ? 'unknown' : 'notApplicable'

const obtentionOf = (certificate: {
  status: string
  automated: boolean
  filePath: string | null
  failureKind?: string | null
}): ObtentionState => {
  if (certificate.status === 'running') return 'running'

  if (certificate.status === 'failed') {
    return certificate.failureKind === 'missingData' ? 'missingData' : 'failed'
  }

  // A refusal is recorded as an adverse result, because that is what it is —
  // but the screen still says why no document came.
  if (certificate.failureKind === 'refused') return 'refused'

  // Resolved means a verdict was recorded — the document was read.
  if (['clear', 'warning', 'adverse'].includes(certificate.status)) return 'obtained'

  // A file with no verdict yet is the portal having answered with a pendency
  // rather than a document, or the reading still to happen.
  if (certificate.filePath) return 'issue'

  return certificate.automated ? 'pending' : 'manual'
}
